import * as React from 'react'

import { Box, CollapsableCard, Text, TogglerCheckbox } from '@factorialco/deprecated-design-system'
import { FormikProps } from 'formik'

import { observer } from 'mobx-react'

import HelpCenterLink from 'modules/core/components/HelpCenterLink'
import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import { useEffect } from 'modules/core/hooks/useEffect'
import hasFeature from 'modules/core/lib/hasFeature'
import i18n from 'modules/core/lib/i18n'

import ManualBreaks from 'modules/attendance/components/SettingItemPage/TimeTracking/BreakSettings/ManualBreaks'
import PolicyBreakConfigurations from 'modules/attendance/components/SettingItemPage/TimeTracking/PolicyBreakConfigurations'
import { TimeSettingsBreakConfigs } from 'modules/attendance/hooks/useFetchTimeSettingsBreakConfigurations'
import { BreakSettingsFormValues } from 'modules/attendance/lib/forms/BreakSettingForm'
import { useTimeTrackingFeatureAvailability } from 'modules/attendance/lib/useTimeTrackingFeatureAvailability'
import type AttendanceSetting from 'modules/attendance/store/models/AttendanceSetting'

interface Props {
  attendanceSetting: AttendanceSetting
  timeSettingsBreakConfigurations: TimeSettingsBreakConfigs[]
  settingForm: FormikProps<BreakSettingsFormValues>
  refetch: () => void
}

const ManualBreak = ({
  attendanceSetting,
  timeSettingsBreakConfigurations,
  settingForm,
  refetch,
}: Props) => {
  const hasPaidBreaksFeature = hasFeature('time_tracking_paid_breaks')
  const automaticBreaksEnabled = useTimeTrackingFeatureAvailability(
    'time_tracking_automatic_breaks'
  )

  const attendanceSettingBreakEnabled = attendanceSetting?.hasBreaksEnabled

  const manualBreakValue = settingForm.values.manualBreaks

  const hasBreaks = timeSettingsBreakConfigurations.length
  const disableManualBreaks = hasPaidBreaksFeature && !hasBreaks

  const [manualBreakEnabled, setBreaksEnabled] = React.useState(
    (attendanceSettingBreakEnabled && manualBreakValue) ?? false
  )

  const handleManualBreaksToggle = async (updatedValue: boolean) => {
    settingForm.setFieldValue('manualBreaks', updatedValue)

    if (automaticBreaksEnabled && updatedValue) {
      settingForm.setFieldValue('automaticBreaks', false)
    }

    if (!updatedValue) {
      settingForm.setFieldValue('restTime', false)
    }

    await attendanceSetting.update({ enable_breaks: updatedValue })

    setBreaksEnabled(updatedValue && attendanceSetting?.hasBreaksEnabled)

    const result = await onConfirm()
    if (result.success) {
      refetch()
    }
  }

  const { running: submitting, performAction: onConfirm } = useAsyncAction(
    async () => {
      await settingForm.submitForm()
    },
    { successFlash: false }
  )

  useEffect(() => {
    setBreaksEnabled(attendanceSetting?.hasBreaksEnabled && settingForm.values.manualBreaks)
  }, [attendanceSetting?.hasBreaksEnabled, settingForm.values.manualBreaks])

  return (
    <>
      {!hasPaidBreaksFeature && (
        <ManualBreaks settingForm={settingForm} toggleEnableBreaks={handleManualBreaksToggle} />
      )}

      {hasPaidBreaksFeature && (
        <CollapsableCard
          open={manualBreakEnabled}
          Card={
            <Box gap="s8">
              <Box flexDirection="row" justifyContent="spaceBetween">
                <Text.Normal weight="medium">
                  {i18n.t('time_tracking.settings.breaks.instance.manual_breaks.label')}
                </Text.Normal>
                <TogglerCheckbox
                  value={manualBreakEnabled}
                  onChange={(value) => handleManualBreaksToggle(value)}
                  disabled={disableManualBreaks || submitting}
                  labelBefore
                />
              </Box>
              <Text.Normal color="grey800" htmlTag="div">
                {i18n.t('time_tracking.settings.breaks.instance.manual_breaks.description')}
              </Text.Normal>
              {hasPaidBreaksFeature && !hasBreaks && (
                <Text.Normal>{i18n.t('time_tracking.settings.custom_breaks.empty')}</Text.Normal>
              )}
              <HelpCenterLink name="manual_breaks">
                {i18n.t('time_tracking.settings.breaks.learn_more')}
              </HelpCenterLink>
            </Box>
          }
          Content={
            <PolicyBreakConfigurations
              attendanceSetting={attendanceSetting}
              timeSettingsBreakConfigurations={timeSettingsBreakConfigurations}
            />
          }
        />
      )}
    </>
  )
}

export default observer(ManualBreak)
